import { IAgencyFormData } from "agency/model"
import { IEventModule } from "app/event/model"
import { LOCALES } from "app/language/model"
import { CURRENCIES } from "currency/model"
import { DISTANCE_UNITS } from "form/map/model"
import { IFormContext } from "form/model"
import { AuthAPI, UserAPI } from "generated-api"

export const USER_ALREADY_EXISTS_ERROR = "user.error.already.exist"
export const USER_ROLE_ALREADY_EXISTS_ERROR = "user.role.error.already.exist"
export const ROLE_NOT_FOUND_ERROR = "role not found"
export const INVALID_CREDENTIALS = "user.current.password.incorrect"

export const LOCAL_STORAGE_TOKEN: string = "local-storage-token"
export const LOCAL_STORAGE_REMEMBER_ME: string = "local-storage-remember-me"

export const LOGIN_PAGE_ID = "login-page"
export const FORGOT_PASSWORD_PAGE_ID = "forgot-password-page"
export const SIGN_UP_PAGE_ID = "sign-up-page"
export const SIGN_UP_VERIFICATION_PAGE_ID = "sign-up-verification-page"

export const EMPTY_TOKEN_VALUE: string = "-1"
export const EMPTY_TOKEN_EXPIRATION_VALUE: Date = new Date(3000, 0)
export const EMPTY_USER_ID: number = -1
export const BAD_CREDENTIAL_ERROR_KEY: string = "user.login.error.credential"
export const NO_ORGANIZATION_ERROR_KEY: string = "user.login.error.no.organization"
export const UNKNOWN_ERROR_KEY: string = "user.login.error.server"
export const NO_RIGHT_ERROR_KEY: string = "user.login.error.no.right"

export const EMAIL_REGEX: RegExp = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

export const FAHRENHEIGHT_CURRENCIES: string[] = [CURRENCIES.CAD, CURRENCIES.USD]

export const COUNTRIES_WITCH_REQUIRE_STATE = ["US", "CA"]

export type IUser = NonNullable<Awaited<ReturnType<typeof UserAPI.getApiUser>>>
export type IUserDTO = Awaited<ReturnType<typeof AuthAPI.postApiAuthLogin>>
export type IUserRole = Awaited<ReturnType<typeof UserAPI.postApiUserRole>>["role"]
export type IUserPermission = IUserDTO["permissions"][0]
export type IUserUiSettings = IUser["ui_settings"]
export type IUserImpersonator = Awaited<ReturnType<typeof UserAPI.getApiUserImpersonatorList>>[0]
export type IUserNewImpersonatorParams = Awaited<Parameters<typeof UserAPI.postApiUserImpersonator>>[0]
export type IUserImpersonateSession = Awaited<ReturnType<typeof AuthAPI.postApiAuthImpersonate>> & {
  firstname: string
  lastname: string
}
export type IAgencyChangeSession = Awaited<ReturnType<typeof AuthAPI.postApiAuthChangeAgency>> & {
  firstname: string
  lastname: string
}

export type IUserImpersonatorHydrated = IUserImpersonator & {
  email: string
  firstname: string
  lastname: string
}

export enum USER_EVENTS {
  LOGIN = "login",
  LOGOUT = "logout",
  DELETE = "delete",
  RENEW_SESSION = "renew-session",
  DISTANCE_UNIT_CHANGE = "distance-unit-change"
}

export enum USER_ROLES {
  DSP_USER = "dsp.user",
  SSP_USER = "ssp.user",
  SALE = "dsp.sale",
  REPORTS = "dsp.reports"
}

export interface IUserFormData extends Partial<IUser> {
  email: string
  agency_id: number
  role: USER_ROLES
}

export type IUserFormDataErrors = {
  [k in keyof IUserFormData]?: string
}

export interface IUserSignupFormData {
  email: string
  firstname: string
  lastname: string
  password1: string
  password2: string
  language_id: LOCALES
  phone: string
}

export type IUserSignupFormDataErrors = {
  [k in keyof IUserSignupFormData]?: string
}

export type IUserImpersonatorFormDataErrors = {
  [k in keyof IUserNewImpersonatorParams]?: string
}

export interface IUserShort {
  id: number
  firstname: string | null
  lastname: string | null
  email?: string
}

export interface IUserInfo {
  user: IUser
  token: string
  tokenExpirationDateTime: Date
  picture: string
  permissions: IUserPermission[]
  impersonate?: IUserImpersonateSession
}

export interface IUserService {
  getUserName(): string
  getPreference(name: string): Promise<string | null>
  hasPreference(name: string): Promise<boolean>
  removePreference(name: string): Promise<string | null>
  setPreference(name: string, value: string | undefined): Promise<void>
  login(email: string, clearPassword: string, rememberMe: boolean): Promise<IUserInfo>
  logout(): Promise<boolean>
  isLoggedIn(): boolean
  getToken(): string
}

export const FIRST_SIGNUP_WIZARD_STEP = 0
export const SECOND_SIGNUP_WIZARD_STEP = 1
export const LAST_SIGNUP_WIZARD_STEP = 2

export enum SIGNUP_WIZARD_STEPS {
  FIRST = FIRST_SIGNUP_WIZARD_STEP,
  SECOND = SECOND_SIGNUP_WIZARD_STEP,
  THIRD = LAST_SIGNUP_WIZARD_STEP
}

const ALL_SIGNUP_WIZARD_STEPS: SIGNUP_WIZARD_STEPS[] = [
  FIRST_SIGNUP_WIZARD_STEP,
  SECOND_SIGNUP_WIZARD_STEP,
  LAST_SIGNUP_WIZARD_STEP
]
export const getAllSignUpWizardSteps = () => ALL_SIGNUP_WIZARD_STEPS.slice()

export interface ISignUpUserForm extends IFormContext<IUserSignupFormData> {
  data: IUserSignupFormData
}

export interface ISignUpAgencyForm extends IFormContext<IAgencyFormData> {
  data: IAgencyFormData
}

export enum SIGNUP_STATUS {
  IN_PROGRESS = "in-progress", // user is entering info
  REQUESTING = "requesting", // server request
  SUCCESS = "success",
  ERROR = "error",
  ERROR_ALREADY_EXIST = "error-already-exist"
}

export interface IUserSignUpState {
  step: SIGNUP_WIZARD_STEPS
  status: SIGNUP_STATUS
  userForm: ISignUpUserForm
  agencyForm: ISignUpAgencyForm
}

export interface IUserState extends IUserInfo {
  isLoggingIn: boolean
  isReLoginPopupOpened: boolean
  signup: IUserSignUpState
}

export interface IUserServiceState {
  sessionKeeperTimeout: NodeJS.Timeout | undefined
  localStorageAvailable: boolean
}

export interface IPasswordChange {
  email: string
  currentClearPassword: string
  newClearPassword: string
}

export interface IUserRepository {
  login(email: string, clearPassword: string): Promise<IUserInfo>
  getUserInfoFromToken(token: string): Promise<IUserInfo | undefined>
  logout(): Promise<void>
  renewSession(email: string): Promise<IUserInfo>
  impersonateSession(email: string): Promise<IUserImpersonateSession>
  changeAgencySession(email: string, agencyId: number): Promise<IAgencyChangeSession>
  getUserById(userId: number, isRequesterAdmin: boolean): Promise<IUser>
  getAgencyUsers(agencyId: number): Promise<IUser[]>
  createUser(email: string, data: Partial<IUser>, clearPassword: string): Promise<IUser>
  updateUser(userId: number, partialUser: Partial<IUser>, passwordChange?: IPasswordChange): Promise<IUser>
  getUserRoles(userId: number): Promise<IUserRole[]>
  addUserRole(userId: number, role: IUserRole): Promise<void>
  deleteUserRole(userId: number, role: IUserRole): Promise<void>
  getAvailableImpersonators(userId: number): Promise<IUserImpersonator[]>
  getImpersonators(userId: number): Promise<IUserImpersonator[]>
  addImpersonator(params: IUserNewImpersonatorParams): Promise<IUserImpersonator>
  updateImpersonator(params: IUserImpersonator): Promise<IUserImpersonator>
  deleteImpersonator(selfUserId: number, allowedUserId: number): Promise<void>
  createAdstandingUser(user: IUserSignupFormData, agency: IAgencyFormData, captcha: string): Promise<void>
  confirmAdstandingUserEmail(email: string, code: string): Promise<IUser>
  resetPasswordDemand(email: string, captcha: string): Promise<void>
  resetPassword(email: string, code: string, password: string, captcha: string): Promise<void>
}

export interface IUserEventLoginParams {
  userInfo: IUserInfo
}

export interface IUserEventDistanceUnitChangeParams {
  oldDistanceUnit: DISTANCE_UNITS
  newDistanceUnit: DISTANCE_UNITS
}

export interface IUserEventModule extends IEventModule {
  on: {
    login(listenerId: string, fn: (params: IUserEventLoginParams) => void): void
    renewSession(listenerId: string, fn: (params: IUserEventLoginParams) => void): void
    logout(listenerId: string, fn: () => void): void
    distanceUnitChange(listenerId: string, fn: (params: IUserEventDistanceUnitChangeParams) => void): void
  }
}

export interface IUserCreationResult {
  user: IUser
  clearPassword: string
}
